import { useState, useCallback, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ResponsiveContainer } from "recharts";

// Material-UI
import { IconButton, Stack } from "@mui/material";
// Types
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import { IMetricsData } from "@/Dashboard/helpers/capitalizeObjectKeys";
// Components
import { CustomLegend } from "./CustomLegend";
import { ChartLineTooltip } from "./ChartLineTooltip";
// Icons
import ZoomReset from "@/assets/icons/dashboard/zoomReset.svg";

const roundNumber = (num: number) => {
	if (num <= 0) {
		return 0;
	}
	if (num < 10) {
		return 10;
	}
	const magnitude = Math.pow(10, Math.floor(Math.log10(num)));
	return Math.round(num / magnitude) * magnitude;
};

const getNextTopValue = (num: number) => {
	return roundNumber(num);
};

const getTopValue = (data: IMetricsData[]) => {
	let highestValue = 0;
	data.forEach((item) => {
		if (item.Dealership && item.Dealership > highestValue) {
			highestValue = item.Dealership;
		}

		if (item.Region && item.Region > highestValue) {
			highestValue = item.Region;
		}
	});

	return highestValue;
};

const getBottomValue = (data: IMetricsData[]) => {
	let lowestValue = data[0]?.Dealership ?? 0;
	data.forEach((item) => {
		if (item.Dealership && item.Dealership < lowestValue) {
			lowestValue = item.Dealership;
		}

		if (item.Region && item.Region < lowestValue) {
			lowestValue = item.Region;
		}
	});

	return lowestValue;
};

type IValidAxisDomain = number | "dataMin" | "dataMax" | "auto";

interface IChartParams {
	refAreaLeft?: number | "";
	refAreaRight?: number | "";
	left?: IValidAxisDomain;
	data: IMetricsData[];
	right?: IValidAxisDomain;
	chartKey?: string;
}

interface IChartLineProps extends IChartParams {
	showYouInfo: boolean;
	hide: IChartLineHideData;
	setHide: (value: IChartLineHideData) => void;
}

export interface IChartLineHideData {
	You: boolean;
	Dealership: boolean;
	Region: boolean;
}

export const ChartLine = (props: IChartLineProps) => {
	const [params, setParams] = useState<IChartParams>({
		data: props.data,
		refAreaLeft: props.refAreaLeft ?? "",
		refAreaRight: props.refAreaRight ?? "",
		left: props.left ?? "dataMin",
		right: props.right ?? "dataMax",
	});

	const customXAxisTick = useCallback(
		(num: number) => {
			const element = params.data?.filter((obj) => obj.name === Math.ceil(num));
			if (!element.length) {
				return "";
			}
			return element[0]?.key.charAt(0);
		},
		[params]
	);

	const zoom = useCallback(() => {
		let { refAreaLeft, refAreaRight } = params;
		if (!refAreaLeft || !refAreaRight) {
			return;
		}

		if (refAreaLeft === refAreaRight) {
			setParams((oldParams) => ({ ...oldParams, refAreaLeft: "", refAreaRight: "" }));
			return;
		}

		if (refAreaLeft > refAreaRight) {
			[refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft];
		}

		setParams({
			...params,
			data: props.data.slice(refAreaLeft - 1, refAreaRight),
			refAreaLeft: "",
			refAreaRight: "",
			left: refAreaLeft,
			right: refAreaRight,
		});
	}, [params, props]);

	const zoomOut = useCallback(() => {
		setParams({
			data: props.data.slice(),
			refAreaLeft: "",
			refAreaRight: "",
			left: 1,
			right: props.data.length,
		});
	}, [props]);

	const onChartMouseDown: CategoricalChartFunc = useCallback(
		(state) => {
			if (state.activeLabel) {
				setParams({ ...params, refAreaLeft: Number(state.activeLabel) });
			}
		},
		[params]
	);

	const onChartMouseMove: CategoricalChartFunc = useCallback(
		(state) => {
			if (state.activeLabel && params.refAreaLeft) {
				setParams({ ...params, refAreaRight: Number(state.activeLabel) });
			}
		},
		[params]
	);

	useEffect(() => {
		if (props.data) {
			zoomOut();
		}
	}, [props, zoomOut]);

	return (
		<Stack className="relative">
			<Stack direction="row-reverse" className="absolute -top-[15px] right-0">
				<IconButton onClick={zoomOut}>
					<img src={ZoomReset} width="15px" alt="zoom reset" />
				</IconButton>
			</Stack>

			<ResponsiveContainer key={props.chartKey} width="100%" height={180} className="mt-[13px]">
				<LineChart
					key={`${props.chartKey}-${params.left}-${params.right}`}
					data={params.data}
					margin={{ top: 4, right: 4, left: -23 }}
					onMouseDown={onChartMouseDown}
					onMouseMove={onChartMouseMove}
					onMouseUp={zoom}
				>
					<CartesianGrid strokeDasharray={"3 3"} stroke="#ccc" />
					<XAxis
						dataKey="name"
						domain={["auto", "auto"]}
						tickFormatter={customXAxisTick}
						type="number"
						tickCount={params.data.length ?? 12}
						stroke="#666"
					/>
					<YAxis
						key={`${props.chartKey}-YAxis-1`}
						domain={[Math.floor(getBottomValue(props.data)), getNextTopValue(getTopValue(props.data))]}
						yAxisId={1}
						type="number"
						stroke="#666"
					/>
					<Tooltip offset={30} content={<ChartLineTooltip />} />
					<Line yAxisId={1} hide={props.hide.Region} type="monotone" dataKey="Region" stroke="#ffa000" strokeWidth={1.5} />
					{props?.showYouInfo && (
						<Line yAxisId={1} hide={props.hide.You} type="monotone" dataKey="You" stroke="#019bd9" strokeWidth={1.5} />
					)}
					<Line
						yAxisId={1}
						hide={props.hide.Dealership}
						type="monotone"
						dataKey="Dealership"
						stroke="#e41f75"
						strokeWidth={1.5}
					/>
					{params.refAreaLeft && params.refAreaRight && (
						<ReferenceArea yAxisId={1} x1={params.refAreaLeft} x2={params.refAreaRight} strokeOpacity={0.3} />
					)}
				</LineChart>
			</ResponsiveContainer>
			<CustomLegend showYouInfo={props.showYouInfo} hide={props.hide} setHide={props.setHide} />
		</Stack>
	);
};
